import { isEqual, xorBy } from 'lodash-es';
import { computed, nextTick, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

export default <T>(selectedElements: Ref<T[]>) => {
  const { t } = useI18n();
  const anchorIndex = ref(null);
  const lastShiftClickedIndex = ref(null);

  const handleMultiSelect = async (index: number, event: MouseEvent, allElements: T[]) => {
    await nextTick();
    if (!event.shiftKey) {
      // Regular click, reset shift tracking & set new anchor index
      lastShiftClickedIndex.value = null;
      anchorIndex.value = index;
      selectedElements.value = xorBy(selectedElements.value, [allElements[index]], 'id');
      return;
    }
    if (anchorIndex.value === null) {
      // If no previous index, select from the start to the current index.
      selectedElements.value = allElements.slice(0, index + 1);
      // Set that as the new anchor index
      anchorIndex.value = index;
    } else {
      // Calculate the new selection range.
      const start = Math.min(anchorIndex.value, index);
      const end = Math.max(anchorIndex.value, index);
      const newRange = allElements.slice(start, end + 1);

      // Determines the complete range that might be affected by the current and previous shift click
      const fullStart =
        lastShiftClickedIndex.value !== null
          ? Math.min(anchorIndex.value, lastShiftClickedIndex.value)
          : anchorIndex.value;
      const fullEnd =
        lastShiftClickedIndex.value !== null
          ? Math.max(anchorIndex.value, lastShiftClickedIndex.value)
          : anchorIndex.value;
      const modifiedRange = allElements.slice(
        Math.min(fullStart, start),
        Math.max(fullEnd, end) + 1,
      );

      const filteredElements =
        selectedElements.value?.filter(
          (selectedElement) =>
            !modifiedRange.some((modifiedElement) => isEqual(selectedElement, modifiedElement)),
        ) ?? [];
      // Combine removal of items within the modified range && addition of the new range.
      selectedElements.value = [
        // Filter out items from selectedItems that match any item in modifiedRange
        ...filteredElements,
        // Then, add the newRange
        ...newRange,
      ].filter(
        // Filter out duplicates
        (item, index, self) => index === self.findIndex((otherItem) => isEqual(item, otherItem)),
      );
    }
    // Update last shift clicked index.
    // Dont update anchorIndex as we keep it on the element that was first clicked.
    lastShiftClickedIndex.value = index;
  };

  // Tooltip:
  const disableTooltip = ref(false);
  const multiSelectTooltip = computed(() => {
    if (disableTooltip.value) {
      return;
    }
    return {
      value: t(`multiElementSelect.tooltip`),
      class: 'list-cell-tooltip',
      escape: true,
    };
  });
  const multiSelectOnMouseLeaveListener = () => {
    disableTooltip.value = true;
  };
  const resetAnchor = () => {
    anchorIndex.value = null;
    lastShiftClickedIndex.value = null;
  };
  return {
    multiSelectTooltip,
    multiSelectOnMouseLeaveListener,
    handleMultiSelect,
    resetAnchor,
  };
};
